import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Users, 
  MessageSquare, 
  Target, 
  DollarSign, 
  TrendingUp, 
  Crown,
  Activity,
  UserPlus,
  MessagesSquare,
  CreditCard,
  ChevronRight,
  Loader2,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

const API = "/api";

// Modern Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendValue, subtext }) => (
  <div className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-white/10 transition-all duration-300">
    <div className="flex items-start justify-between mb-4">
      <div className="p-2.5 rounded-lg bg-[#14A800]/10">
        <Icon className="w-5 h-5 text-[#14A800]" />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-xs font-medium ${
          trend === "up" ? "text-green-400" : "text-red-400"
        }`}>
          {trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trendValue}
        </div>
      )}
    </div>
    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{title}</p>
    <p className="text-2xl font-semibold text-white tracking-tight">{value}</p>
    {subtext && <p className="text-zinc-600 text-xs mt-2">{subtext}</p>}
  </div>
);

// Activity Item Component
const ActivityItem = ({ activity }) => {
  const getIcon = () => {
    switch (activity.type) {
      case "signup": return <UserPlus className="w-4 h-4 text-green-400" />;
      case "conversation": return <MessagesSquare className="w-4 h-4 text-blue-400" />;
      case "lead": return <Target className="w-4 h-4 text-purple-400" />;
      case "payment": return <CreditCard className="w-4 h-4 text-yellow-400" />;
      default: return <Activity className="w-4 h-4 text-zinc-500" />;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors px-2 -mx-2 rounded">
      <div className="p-2 bg-zinc-900 rounded-lg">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-300 truncate">{activity.description}</p>
        <p className="text-xs text-zinc-600 font-mono">{activity.email}</p>
      </div>
      <span className="text-xs text-zinc-600 flex-shrink-0">{formatTime(activity.timestamp)}</span>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return;
    }
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      // Fetch stats
      const statsRes = await fetch(`${API}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (statsRes.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }
      
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch activities
      const actRes = await fetch(`${API}/admin/dashboard/activity?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const actData = await actRes.json();
      setActivities(actData.activities || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#14A800]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Page Header */}
      <div className="border-b border-white/5 bg-[#0a0a0a]/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="px-8 py-6">
          <h1 className="text-2xl font-semibold text-white tracking-tight">Dashboard</h1>
          <p className="text-zinc-500 text-sm mt-1">Platform overview and key metrics</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        {/* Primary KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Users"
            value={stats?.total_users || 0}
            icon={Users}
            trend="up"
            trendValue={`${stats?.active_users_30d || 0} active`}
          />
          <StatCard
            title="Conversations"
            value={stats?.total_conversations || 0}
            icon={MessageSquare}
            subtext={`${stats?.conversations_this_month || 0} this month`}
          />
          <StatCard
            title="Leads Captured"
            value={stats?.total_leads || 0}
            icon={Target}
          />
          <StatCard
            title="Revenue"
            value={`$${stats?.total_revenue || 0}`}
            icon={DollarSign}
            trend="up"
            trendValue={`$${stats?.revenue_this_month || 0} MTD`}
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard
            title="Pro Subscribers"
            value={stats?.pro_subscribers || 0}
            icon={Crown}
          />
          <StatCard
            title="Free Users"
            value={stats?.free_users || 0}
            icon={Users}
          />
          <StatCard
            title="Monthly Recurring"
            value={`$${stats?.revenue_this_month || 0}`}
            icon={TrendingUp}
            subtext={stats?.current_month}
          />
        </div>

        {/* Activity & Quick Actions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-[#121212] border border-white/5 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-medium text-white">Recent Activity</h2>
              <Activity className="w-4 h-4 text-zinc-600" />
            </div>
            
            <div className="p-4 max-h-[400px] overflow-y-auto">
              {activities.length > 0 ? (
                activities.map((activity, index) => (
                  <ActivityItem key={index} activity={activity} />
                ))
              ) : (
                <p className="text-zinc-600 text-center py-8">No recent activity</p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/5">
              <h2 className="text-lg font-medium text-white">Quick Actions</h2>
            </div>
            
            <div className="p-4 space-y-2">
              <Link
                to="/admin/users"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Users className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-zinc-300 text-sm">Manage Users</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-white transition-colors" />
              </Link>
              
              <Link
                to="/admin/leads"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Target className="w-4 h-4 text-purple-400" />
                  </div>
                  <span className="text-zinc-300 text-sm">View Leads</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-white transition-colors" />
              </Link>
              
              <Link
                to="/admin/analytics"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-zinc-300 text-sm">View Analytics</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-white transition-colors" />
              </Link>
              
              <button
                onClick={fetchDashboardData}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-zinc-800 rounded-lg">
                    <Activity className="w-4 h-4 text-zinc-400" />
                  </div>
                  <span className="text-zinc-300 text-sm">Refresh Data</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-white transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
